import { gql, type TypedDocumentNode } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconPlayCircle } from '@sorare/core/src/atoms/icons/system/iconPlayCircle.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { ANY_SPORT_MARKET_WHEEL_RARITY } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useEnabledWheelByRarity } from '@sorare/core/src/hooks/useEnabledWheelByRarity';
import { isType } from '@sorare/core/src/lib/gql';
import { useWheelTickets } from '@sorare/marketplace/src/hooks/wheel/useWheelTickets';

import { DailyActionTask } from '#src/components/missions/DailyActionTask/index.tsx';
import { FeaturedCollectionTask } from '#src/components/missions/FeaturedCollectionTask/index.tsx';
import { FeaturedPlayTask } from '#src/components/missions/FeaturedPlayTask/index.tsx';
import { PlayMissionsDialog } from '#src/components/missions/PlayMissionsDialog/index.tsx';
import { UnclaimedDeliverableItemOrderButton } from '#src/components/rewards/deliverableItem/UnclaimedDeliverableItemOrders/UnclaimedDeliverableItemOrder/index.tsx';
import { FeaturedRaffleTask } from '#src/components/series/raffle/FeaturedRaffleTask/index.tsx';

import type {
  SeriesMissions_cardSetRoot,
  SeriesMissions_currentUser,
  SeriesMissions_deliverableItemOrder,
} from './__generated__/index.graphql.ts';

type Props = {
  currentUser: SeriesMissions_currentUser;
  cardSet?: SeriesMissions_cardSetRoot | null;
  deliverableItemOrder?: SeriesMissions_deliverableItemOrder;
  showFeaturedCollectionTask: boolean;
  showFeaturedPlayTask?: boolean;
};
export const SeriesMissions = ({
  currentUser,
  cardSet,
  deliverableItemOrder,
  showFeaturedCollectionTask,
  showFeaturedPlayTask = true,
}: Props) => {
  const { ticketCount } = useWheelTickets();
  const { common: isCommonWheelEnabled } = useEnabledWheelByRarity();
  const { generateSportContextPath } = useSportContext();
  const [playMissionsDialogOpen, setPlayMissionsDialogOpen] = useState(false);

  const playDailyActionTask = isType(
    currentUser?.dailyRunTask,
    'DailyActionTask'
  )
    ? currentUser?.dailyRunTask
    : null;
  const featuredPlayTask = currentUser?.setPlayTasks?.at(0);

  const featuredCollectionTask = currentUser?.setCollectionsTaskTracks?.at(0);

  const { currentRaffle } = cardSet || {};

  const featuredRaffleTask = currentRaffle?.myRaffleTasks?.at(0);

  if (
    !playDailyActionTask &&
    !featuredCollectionTask &&
    !featuredPlayTask &&
    !featuredRaffleTask
  ) {
    return null;
  }

  return (
    <>
      <Vertical gap={2}>
        {!!deliverableItemOrder && (
          <UnclaimedDeliverableItemOrderButton
            deliverableItemOrder={deliverableItemOrder}
          />
        )}
        {!!playDailyActionTask && (
          <DailyActionTask
            task={playDailyActionTask}
            onDecisivePlayerPickerClick={() => setPlayMissionsDialogOpen(true)}
            onDropChallengeClick={() => {}}
          >
            {isCommonWheelEnabled ? (
              <Button
                size="medium"
                color={ticketCount > 0 ? 'secondary' : 'quaternary'}
                to={generateSportContextPath(ANY_SPORT_MARKET_WHEEL_RARITY, {
                  params: {
                    rarity: Rarity.common,
                  },
                })}
                disabled={ticketCount === 0}
              >
                <FontAwesomeIcon icon={iconPlayCircle} />
                <FormattedMessage defaultMessage="Spin the wheel" />
              </Button>
            ) : null}
          </DailyActionTask>
        )}
        {!!currentRaffle && <FeaturedRaffleTask task={featuredRaffleTask} />}
        {!!featuredCollectionTask && !!showFeaturedCollectionTask && (
          <FeaturedCollectionTask task={featuredCollectionTask} />
        )}
        {!!featuredPlayTask && !!showFeaturedPlayTask && (
          <FeaturedPlayTask task={featuredPlayTask} />
        )}
      </Vertical>
      <PlayMissionsDialog
        open={playMissionsDialogOpen}
        onClose={() => setPlayMissionsDialogOpen(false)}
      />
    </>
  );
};

SeriesMissions.fragments = {
  currentUser: gql`
    fragment SeriesMissions_currentUser on CurrentUser {
      slug
      dailyRunTask(sport: $sport) {
        id
        name
        ... on DailyActionTask {
          id
        }
        ...DailyActionTask_taskInterface
      }
      setCollectionsTaskTracks(sport: $sport) {
        id
        ...FeaturedCollectionTask_taskInterface
      }
      setPlayTasks(sport: $sport) {
        id
        ...FeaturedPlayTask_taskInterface
      }
    }
    ${DailyActionTask.fragments.taskInterface}
    ${FeaturedCollectionTask.fragments.taskInterface}
    ${FeaturedPlayTask.fragments.taskInterface}
  ` as TypedDocumentNode<SeriesMissions_currentUser>,
  cardSet: gql`
    fragment SeriesMissions_cardSetRoot on CardSetRoot {
      id
      currentRaffle(sport: $sport) {
        slug
        myRaffleTasks {
          id
          ...FeaturedRaffleTask_taskInterface
        }
      }
    }
    ${FeaturedRaffleTask.fragments.taskInterface}
  ` as TypedDocumentNode<SeriesMissions_cardSetRoot>,
  deliverableItemOrder: gql`
    fragment SeriesMissions_deliverableItemOrder on DeliverableItemOrder {
      id
      ...UnclaimedDeliverableItemOrderButton_deliverableItemOrder
    }
    ${UnclaimedDeliverableItemOrderButton.fragments.deliverableItemOrder}
  ` as TypedDocumentNode<SeriesMissions_deliverableItemOrder>,
};
